import type { ColumnDef } from '@tanstack/react-table';
import { SETTINGS_PERMISSIONS } from '@/constants/permissions';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import {
  createActionsColumn,
  createSerialColumn,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowEditDeleteButtons } from '@/components/common/RowActions';
import { CODE_BADGE_CLASS } from '@/styles/codeBadge';
import { formatDisplayDate } from '@/utils/date';
import type { LeaveType } from '../types/leaveType.types';

interface LeaveTypeColumnActions {
  onEdit: (leaveType: LeaveType) => void;
  onDelete: (leaveType: LeaveType) => void;
}

export function getLeaveTypeColumns(
  searchText?: string,
  actions?: LeaveTypeColumnActions,
): ColumnDef<LeaveType, unknown>[] {
  return [
    createSerialColumn<LeaveType>(),
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => (
        <WrappingCellText className={CODE_BADGE_CLASS}>
          <HighlightText text={row.original.code} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => {
        const description = row.original.description;
        if (!description) return <EmptyValue />;
        return <WrappingCellText width="wide">{description}</WrappingCellText>;
      },
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => formatDisplayDate(row.original.createdAt),
    },
    ...(actions
      ? [
          createActionsColumn<LeaveType>((leaveType) => (
            <RowEditDeleteButtons
              item={leaveType}
              itemName={leaveType.name}
              onEdit={actions.onEdit}
              onDelete={actions.onDelete}
              editRequires={SETTINGS_PERMISSIONS.LEAVE_TYPES_EDIT}
              deleteRequires={SETTINGS_PERMISSIONS.LEAVE_TYPES_DELETE}
            />
          )),
        ]
      : []),
  ];
}
